import { makeAutoObservable } from 'mobx'
import AuthService from './../../../shared/services/Auth.service'
import env from './../../../environments/environment'

// Technical manual structure: { id, title, filename, category, content, children }
class TechManualsStore {
  constructor() {
    makeAutoObservable(this)
  }

  manuals = []
  manualTree = []
  selectedManual = null
  selectedManualPath = null
  searchQuery = ''
  searchMode = 'global' // 'global' or 'current'
  searchResults = []
  expandedSections = []
  isLoading = false
  manualContentCache = {}

  // Load technical manual structure from a predefined list
  // In a real implementation, this could scan the docs directory or fetch from an API
  loadManuals = async () => {
    this.isLoading = true
    try {
      // Define technical manual structure
      // Files are served via backend API from /functions/docs/tech-manuals/
      const manualManifest = [
        {
          id: 'race-to-royal-ring',
          title: 'Royal Ring Recipients System',
          filename: 'Race-to-the-Royal-Ring.md',
          category: 'Features',
          children: null,
        },
        {
          id: 'carrier-contacts',
          title: 'Carrier Contacts System',
          filename: 'Carrier-Contacts.md',
          category: 'Features',
          children: null,
        },
        {
          id: 'sales-tracker-rewrite-detection',
          title: 'Sales Tracker Rewrite Detection',
          filename: 'Sales-Tracker-Rewrite-Detection.md',
          category: 'Features',
          children: null,
        },
        {
          id: 'productscout-tuning',
          title: 'ProductScout Tuning Guide',
          // TEMP 2026-05-26: backend route `/tech-manuals/:filename` doesn't
          // capture nested paths, so the `modules/` subdir copy 404s. Using
          // a top-level copy until the route + handler are fixed in a
          // properly-scoped ticket. Both files exist; clean up when fixed.
          filename: 'productscout-tuning.md',
          category: 'API Modules',
          children: null,
        },
        {
          id: 'productscout-reviewing-ingested-data',
          title: 'ProductScout — Reviewing Ingested Data',
          // Operator playbook for: "Did this doc get into the corpus?",
          // "Is the extracted text good?", "Why is the AI saying X?".
          // Top-level path because of the same nested-path backend bug.
          filename: 'productscout-reviewing-ingested-data.md',
          category: 'API Modules',
          children: null,
        },
        // Add more technical documentation files here as they're created
        // Example:
        // {
        //   id: 'architecture-overview',
        //   title: 'Architecture Overview',
        //   filename: 'Architecture-Overview.md',
        //   category: 'System',
        //   children: null,
        // },
      ]

      this.manuals = manualManifest
      this.manualTree = this.buildTree(manualManifest)

      // Expand first category by default
      if (this.manualTree.length > 0) {
        this.expandedSections = [this.manualTree[0].id]
      }
    } catch (error) {
      console.error('Failed to load manuals:', error)
      this.manuals = []
      this.manualTree = []
    } finally {
      this.isLoading = false
    }
  }

  buildTree = manuals => {
    const tree = []
    const categoryMap = {}

    manuals.forEach(manual => {
      const category = manual.category || 'Other'
      if (!categoryMap[category]) {
        categoryMap[category] = {
          id: `category-${category.toLowerCase().replace(/\s+/g, '-')}`,
          title: category,
          type: 'category',
          children: [],
        }
        tree.push(categoryMap[category])
      }
      categoryMap[category].children.push(manual)
    })

    return tree
  }

  selectManual = async manualId => {
    // Find manual by ID
    const manual = this.manuals.find(m => m.id === manualId)
    if (!manual) {
      console.error('Manual not found:', manualId)
      return
    }

    // Check cache
    if (this.manualContentCache[manualId]) {
      this.selectedManual = this.manualContentCache[manualId]
      this.selectedManualPath = manualId
      return
    }

    this.isLoading = true
    try {
      // Fetch technical manual from API with authentication
      // Note: env.api.base_url already includes /api/ with trailing slash
      const apiUrl = `${env.api.base_url}tech-manuals/${manual.filename}`

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: 'Bearer ' + AuthService.getToken(),
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to load manual: ${response.statusText}`)
      }

      const content = await response.text()

      const manualData = {
        ...manual,
        content,
      }

      this.manualContentCache[manualId] = manualData
      this.selectedManual = manualData
      this.selectedManualPath = manualId
    } catch (error) {
      console.error('Failed to load manual:', error)
      this.selectedManual = null
      this.selectedManualPath = null
    } finally {
      this.isLoading = false
    }
  }

  search = query => {
    this.searchQuery = query

    if (!query || query.trim() === '') {
      this.searchResults = []
      return
    }

    const normalizedQuery = query.toLowerCase().trim()

    if (this.searchMode === 'global') {
      // Search across all manuals
      const results = []
      this.manuals.forEach(manual => {
        if (manual.title.toLowerCase().includes(normalizedQuery)) {
          results.push({
            manual,
            type: 'title',
            match: manual.title,
          })
        }
      })
      this.searchResults = results
    } else {
      // Search within current manual
      if (!this.selectedManual || !this.selectedManual.content) {
        this.searchResults = []
        return
      }

      const content = this.selectedManual.content
      const lines = content.split('\n')
      const results = []

      lines.forEach((line, index) => {
        if (line.toLowerCase().includes(normalizedQuery)) {
          results.push({
            line: index + 1,
            text: line.trim(),
            match: line,
          })
        }
      })

      this.searchResults = results
    }
  }

  setSearchMode = mode => {
    this.searchMode = mode
    if (this.searchQuery) {
      this.search(this.searchQuery)
    }
  }

  toggleSection = sectionId => {
    const index = this.expandedSections.indexOf(sectionId)
    if (index > -1) {
      this.expandedSections = this.expandedSections.filter(id => id !== sectionId)
    } else {
      this.expandedSections = [...this.expandedSections, sectionId]
    }
  }

  clearSearch = () => {
    this.searchQuery = ''
    this.searchResults = []
  }
}

export default new TechManualsStore()
